import { useContext } from 'react';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { PluginContext } from 'ts-components/Plugins/context';
import { findMatchingAction } from '@kizenapps/engine/util';
import { useToast, toastVariant } from 'components/ToastProvider';
import { useTranslation } from 'react-i18next';

type JsActionRef = {
  apiName: string;
  pluginApp?: { apiName?: string } | null;
};

type JsAction = {
  id: string;
  apiName: string;
  name: string;
  script: string;
  pluginApp?: { id?: string; apiName?: string } | null;
};

// Builds the composite key used to uniquely identify a JS action:
//   "{pluginApp.apiName}.{action.apiName}"
// When the action is not plugin-owned the key is just ".{action.apiName}".
export const buildJsActionKey = (jsAction: JsActionRef): string =>
  `${jsAction.pluginApp?.apiName || ''}.${jsAction.apiName}`;

// Resolves a JS action key to its action, builds plugin args, and returns
// execute(extraArgs?) — a function that runs the script and returns a
// Promise<recordId | null>.
export const useJsActionOverride = (
  objectId: string,
  {
    jsActions = [],
    overrideKey,
    entityId = '',
  }: { jsActions?: any[]; overrideKey?: string; entityId?: string } = {}
) => {
  const { plugins } = useContext(PluginContext);
  const [showToast] = useToast() as [(...args: any[]) => void, ...any[]];
  const { t } = useTranslation();

  const jsAction = findMatchingAction(
    jsActions,
    overrideKey
  ) as JsAction | null;

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId,
    onError: (error: any) => {
      showToast({
        message:
          error?.message ||
          t('An error occurred while executing the custom script.'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const { pending, indicator } = internalState as {
    pending: boolean;
    indicator: any;
  };

  // Guard against re-entry: if the script is already running (e.g. a slow
  // network call before a modal appears), ignore subsequent calls so dropdown
  // selections that fire before the disabled state propagates don't double-execute.
  const execute = jsAction
    ? async (extraArgs: Record<string, unknown> = {}) => {
        if (pending) return null;
        const pluginApiName = jsAction.pluginApp?.apiName ?? '';
        const pluginConfig = plugins?.[pluginApiName]?.business_config ?? {};
        return executeScript(
          jsAction.script,
          {
            ...pluginConfig,
            ...extraArgs,
            pluginId: jsAction.pluginApp?.id,
            objectId,
          },
          {
            id: jsAction.id,
            api_name: jsAction.apiName,
            plugin_api_name: pluginApiName,
            script_id: jsAction.id,
          }
        );
      }
    : null;

  return { jsAction, execute, pending, indicator };
};
